/**
 * Flèches Précédent / Suivant du navigateur (et bouton Retour d'Android).
 *
 * L'application n'a pas d'adresses par écran : la navigation se fait par des
 * états React (chantier ouvert, onglet, plan ouvert, page, fiche…). Sans ce
 * module, la flèche « Précédent » quitte l'application.
 *
 * Principe : chaque état suivi par `useHistorique` ajoute une entrée dans
 * l'historique du navigateur quand il change. Précédent remet la valeur
 * d'avant, Suivant remet la valeur d'après.
 *
 * - Un bouton de l'application qui ramène un état à sa valeur d'avant
 *   (« ‹ Chantiers », ✕, « ‹ Plans »…) recule dans l'historique au lieu
 *   d'ajouter une entrée : Précédent ne rouvre donc pas ce qu'on vient de fermer.
 * - Les changements faits d'un seul geste (ex. « Voir sur le plan », qui change
 *   d'onglet et ouvre un plan) forment une seule entrée.
 * - Une entrée dont l'écran a disparu entre-temps est sautée.
 */
import { useEffect, useRef } from 'react'

type Vie = { vivant: boolean }
type Etape = {
  source: Vie
  cleAvant: unknown
  annuler: () => void
  refaire: () => void
}
type Entree = { etapes: Etape[] }

const SESSION = Math.random().toString(36).slice(2)
const GROUPE_MS = 250

/** pile[i] fait passer de la position i à la position i + 1. */
const pile: Entree[] = []
let position = 0
let silencieux = 0 // nombre de popstate provoqués par nous, à ne pas traiter
let dernierAjout = 0
let installe = false

function etat(n: number) {
  return { navLarente: SESSION, n }
}

function vivante(e: Entree) {
  return e.etapes.some((x) => x.source.vivant)
}

function installer() {
  if (installe || typeof window === 'undefined') return
  installe = true
  history.replaceState(etat(0), '')
  window.addEventListener('popstate', (ev) => {
    const s = ev.state as { navLarente?: string; n?: number } | null
    dernierAjout = 0
    if (silencieux > 0) {
      silencieux--
      if (s?.navLarente === SESSION && typeof s.n === 'number') position = s.n
      return
    }
    if (!s || s.navLarente !== SESSION || typeof s.n !== 'number') {
      // Entrée laissée par une session précédente (avant un rechargement) : on la saute.
      if (position > 0) silencieux++
      history.back()
      return
    }
    aller(s.n)
  })
}

function aller(cible: number) {
  const recul = cible < position
  while (position > cible) {
    const e = pile[position - 1]
    position--
    if (e) for (const x of [...e.etapes].reverse()) x.annuler()
  }
  while (position < cible) {
    const e = pile[position]
    position++
    if (e) for (const x of e.etapes) x.refaire()
  }
  // L'écran de l'entrée courante a été fermé entre-temps : elle n'a plus d'effet.
  const courante = position > 0 ? pile[position - 1] : null
  if (courante && !vivante(courante)) {
    if (recul) {
      history.back() // on continue de reculer jusqu'à un écran qui existe
    } else {
      silencieux++
      history.back() // Suivant sans effet : on revient où on était
    }
  }
}

function noter(etape: Etape, cleApres: unknown) {
  // 1. Retour par un bouton de l'application : la valeur revient à celle d'avant
  //    la dernière étape de ce même état, et rien de vivant ne s'est ajouté depuis.
  for (let i = position - 1; i >= 0; i--) {
    const e = pile[i]
    const idx = e.etapes.findIndex((x) => x.source === etape.source)
    if (idx >= 0) {
      const x = e.etapes[idx]
      if (Object.is(x.cleAvant, cleApres)) {
        const autres = e.etapes.filter((y, j) => j !== idx && y.source.vivant)
        if (autres.length === 0) {
          // On recule dans l'historique du navigateur, sans rien rejouer.
          const pas = position - i
          position = i
          silencieux++
          history.go(-pas)
        } else {
          // L'entrée faisait aussi autre chose (ex. changer d'onglet) : on n'en retire que cette étape.
          e.etapes.splice(idx, 1)
        }
        dernierAjout = 0
        return
      }
      break
    }
    if (vivante(e)) break
  }

  // 2. Nouvelle étape, regroupée avec la précédente si elle vient du même geste.
  const maintenant = Date.now()
  if (position > 0 && position === pile.length && maintenant - dernierAjout < GROUPE_MS) {
    pile[position - 1].etapes.push(etape)
  } else {
    pile.length = position
    pile.push({ etapes: [etape] })
    position++
    history.pushState(etat(position), '')
  }
  dernierAjout = maintenant
}

/**
 * Suit un état de navigation dans l'historique du navigateur.
 * `cle` sert à comparer les valeurs (ex. l'identifiant d'un chantier, pour
 * qu'un rechargement de la fiche ne compte pas comme une navigation).
 */
export function useHistorique<T>(valeur: T, setValeur: (v: T) => void, cle: (v: T) => unknown = (v) => v) {
  installer()
  const precedent = useRef(valeur)
  const attendu = useRef<{ k: unknown } | null>(null)
  const vie = useRef<Vie>({ vivant: true })
  const set = useRef(setValeur)
  set.current = setValeur
  const fCle = useRef(cle)
  fCle.current = cle

  useEffect(() => {
    const v = vie.current
    v.vivant = true
    return () => {
      v.vivant = false
    }
  }, [])

  useEffect(() => {
    const avant = precedent.current
    precedent.current = valeur
    const kAvant = fCle.current(avant)
    const kApres = fCle.current(valeur)
    if (Object.is(kAvant, kApres)) return
    if (attendu.current && Object.is(attendu.current.k, kApres)) {
      attendu.current = null // changement fait par Précédent / Suivant
      return
    }
    attendu.current = null
    const v = vie.current
    const appliquer = (x: T) => () => {
      if (!v.vivant) return
      const k = fCle.current(x)
      if (Object.is(fCle.current(precedent.current), k)) return
      attendu.current = { k }
      set.current(x)
    }
    noter({ source: v, cleAvant: kAvant, annuler: appliquer(avant), refaire: appliquer(valeur) }, kApres)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valeur])
}
